import {
  Image,
  Button,
  Col,
  Row,
  Typography,
  Form,
  message,
  Space,
  Descriptions,
  Input,
} from "antd";
import { authorizeActions, authorizeSelectors } from "../store/authorize";
import { useDispatch, useSelector } from "../store/store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import {
  getAvatar,
  getProfile,
  updateProfile,
  uploadAvatar,
} from "../repository/auth.repository";
import { UserInput } from "../repository/auth.default";
import BlankAvatar from "../assets/images/blank-avatar.svg";
import ResetPasswordWizard from "../components/ResetPasswordWizard";
import { Icons } from "../assets/icons";
import Icon from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useEntityEditor from "../hooks/useEntityEditor";
import { getProblemMessage } from "../utils/http";
import { FieldConfig, renderFields } from "../controls/DescriptionsFields";
import {
  userIdentityFields,
  userContactFields,
  corporateContactFields,
} from "../controls/userProfileFields";
import EditModeActions from "../components/user-card/EditModeActions";

function UserProfilePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const avatarRef = useRef<HTMLInputElement>(null!);
  const [messageApi, contextHolder] = message.useMessage();

  const user = useSelector(authorizeSelectors.user);
  // Affiliates (consultants/collaborators) have no CompanyUserProfile — hide the corporate-only
  // fields (Company Phone, About Me) for them. In the admin app this is Admin (corporate) vs Consultant.
  const isAffiliate = useSelector(authorizeSelectors.isAffiliate);
  const isAppAdmin = useSelector(authorizeSelectors.isAppAdmin);
  const isCorporate = !isAffiliate;
  const roleLabel = isAffiliate
    ? "Consultant"
    : isAppAdmin
      ? "Administrator"
      : "User";

  const [item, setItem] = useState<Record<string, any> | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  // Lifecycle

  function loadProfile(): void {
    getProfile()
      .then((data) => setItem(data))
      .catch((e: any) => {
        message.error(getProblemMessage(e) || "Failed to load profile.");
      });
  }

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editor = useEntityEditor({
    item,
    updateFn: (input) => updateProfile(input as UserInput),
    onSuccess: () => {
      // Refresh the card AND the store user so the header (avatar menu) reflects the change.
      getProfile().then((data) => {
        setItem(data);
        dispatch(authorizeActions.setUser({ ...(user as any), ...data }));
      });
    },
  });

  useEffect(() => {
    if (!avatarLoading) {
      getAvatar()
        .catch(() => BlankAvatar)
        .then((data) => dispatch(authorizeActions.setAvatar(data)));
    }
  }, [avatarLoading, dispatch]);

  // Events

  function onPickAvatar(): void {
    avatarRef.current.click();
  }

  function onUploadAvatar(e: React.ChangeEvent): void {
    const file = (e.target as any).files[0];
    if (file) {
      const mb = file.size / 1024 / 1024;
      if (mb > 10) {
        messageApi.open({ type: "error", content: "Avatar size limit is 10mb" });
      } else {
        setAvatarLoading(true);
        uploadAvatar(file)
          .then(() => {
            setAvatarLoading(false);
            messageApi.open({ type: "success", content: "Successfully uploaded avatar" });
          })
          .catch(() => {
            setAvatarLoading(false);
            messageApi.open({ type: "error", content: "Failed to upload avatar" });
          });
      }
    }
  }

  // Fields — compose the shared user-profile builders so MyProfile stays consistent with the admin
  // user cards (same confirmed-contact indicators), while keeping the self-editable set correct.
  const identityFields: FieldConfig[] = userIdentityFields({
    dateOfBirthEditable: false,
  });

  const contactFields: FieldConfig[] = [
    ...userContactFields(item, { editable: true }),
    ...(isCorporate ? corporateContactFields(item, { editable: true }) : []),
  ];

  const aboutFields: FieldConfig[] = [
    {
      key: "aboutMe",
      label: "About Me",
      span: 2,
      editable: true,
      input: <Input.TextArea rows={3} />,
    },
  ];

  const avatarSrc = user?.avatarSrc ? user.avatarSrc : BlankAvatar;
  const displayName =
    [item?.firstName, item?.lastName].filter(Boolean).join(" ") || item?.userName;

  return (
    <Col className="flex flex-col overflow-hidden">
      {/* Header */}
      <Row className="flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Profile
        </Typography.Title>
        <Space className="flex flex-row">
          {editor.editing ? (
            <EditModeActions editor={editor} />
          ) : (
            <Button type="primary" onClick={editor.startEditing}>
              Edit
            </Button>
          )}
          <Button
            className="ml-4"
            type={"text"}
            icon={<Icon className="text-xl" component={Icons.CloseIcon} />}
            onClick={() => navigate("../")}
          />
        </Space>
      </Row>

      {/* Content */}
      <Col className="flex flex-col gap-4 overflow-hidden overflow-y-auto p-4 pt-0">
        {/* Identity header (avatar + name + role) */}
        <Row className="flex flex-row items-center gap-4">
          <Col className="relative flex flex-col">
            <Image
              width={96}
              height={96}
              src={avatarSrc}
              alt="Avatar"
              rootClassName="overflow-hidden rounded-full"
              style={{ objectFit: "cover" }}
            />
            <input
              className="hidden"
              ref={avatarRef}
              hidden={true}
              type="file"
              accept="image/png, image/jpeg, image/svg+xml"
              onChange={onUploadAvatar}
            />
            <Col className="absolute -bottom-2 -right-2 opacity-50 hover:opacity-100">
              <Button
                shape="circle"
                size="small"
                icon={<FontAwesomeIcon icon={faImage} />}
                loading={avatarLoading}
                onClick={onPickAvatar}
              />
            </Col>
          </Col>
          <Col className="flex flex-col">
            <Typography.Title level={4} className="!mb-0">
              {displayName}
            </Typography.Title>
            <Typography.Text type="secondary">{roleLabel}</Typography.Text>
          </Col>
        </Row>

        {/* Identity + Contact */}
        <Form form={editor.form} component={false}>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Descriptions
              title="Identity"
              bordered
              size="small"
              column={2}
              items={renderFields(identityFields, item, editor.editing)}
            />
            <Descriptions
              title="Contact"
              bordered
              size="small"
              column={1}
              items={renderFields(contactFields, item, editor.editing)}
            />
          </div>

          {isCorporate && (
            <Descriptions
              className="mt-4"
              title="About"
              bordered
              size="small"
              column={1}
              items={renderFields(aboutFields, item, editor.editing)}
            />
          )}
        </Form>

        {/* Security */}
        <div className="flex flex-col gap-2">
          <Typography.Title level={5} className="m-0">
            Security
          </Typography.Title>
          <Row className="flex flex-row">
            <Button danger onClick={() => setResetPasswordOpen(true)}>
              Reset Password
            </Button>
          </Row>
        </div>
      </Col>

      {/* Reset Password Modal */}
      <ResetPasswordWizard
        open={resetPasswordOpen}
        onCancel={() => setResetPasswordOpen(false)}
        onComplete={() => setResetPasswordOpen(false)}
      />
      {contextHolder}
    </Col>
  );
}

export default UserProfilePage;
